import React, { useEffect, useState, useRef } from 'react'
import './ProgressModal.css'

interface ProgressStep {
  label: string
  targetProgress: number
  duration: number
}

interface ProgressModalProps {
  isOpen: boolean
  mode: 'Quick' | 'Strategy Planning' | 'Strategy Map Regeneration'
  onClose: () => void
  completed?: boolean
}

const MODE_STEPS: Record<'Quick' | 'Strategy Planning' | 'Strategy Map Regeneration', ProgressStep[]> = {
  Quick: [
    { label: 'Classifying intent', targetProgress: 10, duration: 6000 },
    { label: 'Searching knowledge graph', targetProgress: 35, duration: 15000 },
    { label: 'Extracting evidence', targetProgress: 55, duration: 12000 },
    { label: 'Generating strategic insights', targetProgress: 90, duration: 22000 },
    { label: 'Finalizing', targetProgress: 100, duration: 5000 }
  ],
  'Strategy Planning': [
    { label: 'DDCA scope definition', targetProgress: 7, duration: 16000 },
    { label: 'Company intelligence + semantic mapping', targetProgress: 14, duration: 12000 },
    { label: 'Adaptive query planning', targetProgress: 24, duration: 22000 },
    { label: 'Exploring knowledge graph', targetProgress: 40, duration: 25000 },
    { label: 'Assessing relevance + causal analysis', targetProgress: 58, duration: 35000 },
    { label: 'Generating MECE framework', targetProgress: 74, duration: 35000 },
    { label: 'Creating Strategy Map + narratives', targetProgress: 90, duration: 55000 },
    { label: 'Finalizing', targetProgress: 97, duration: 25000 }
  ],
  'Strategy Map Regeneration': [
    { label: 'Loading workflow context', targetProgress: 15, duration: 3000 },
    { label: 'Processing MECE framework', targetProgress: 35, duration: 5000 },
    { label: 'Mapping to BSC perspectives', targetProgress: 55, duration: 8000 },
    { label: 'Generating strategic objectives', targetProgress: 80, duration: 15000 },
    { label: 'Building causal relationships', targetProgress: 95, duration: 10000 },
    { label: 'Finalizing Strategy Map', targetProgress: 100, duration: 3000 }
  ]
}

export const ProgressModal: React.FC<ProgressModalProps> = ({
  isOpen,
  mode,
  onClose,
  completed = false
}) => {
  const [currentStepIndex, setCurrentStepIndex] = useState(0)
  const [progress, setProgress] = useState(0)
  const [elapsedTime, setElapsedTime] = useState(0)
  const stepIntervalRef = useRef<number | null>(null)
  const timerIntervalRef = useRef<number | null>(null)
  const modalRef = useRef<HTMLDivElement>(null)

  const steps = MODE_STEPS[mode]

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setCurrentStepIndex(0)
      setProgress(0)
      setElapsedTime(0)
    }
  }, [isOpen])

  // Handle step advancement
  useEffect(() => {
    if (!isOpen || completed) return

    // Clear any existing interval
    if (stepIntervalRef.current) {
      clearInterval(stepIntervalRef.current)
    }

    // If we've completed all steps, hold at 95%
    if (currentStepIndex >= steps.length) {
      setProgress(95)
      return
    }

    const currentStep = steps[currentStepIndex]
    const stepDuration = currentStep.duration
    const targetProgress = currentStep.targetProgress
    const startProgress = currentStepIndex === 0 ? 0 : steps[currentStepIndex - 1].targetProgress

    const progressIncrement = (targetProgress - startProgress) / (stepDuration / 100)
    let currentProgress = startProgress

    stepIntervalRef.current = setInterval(() => {
      currentProgress += progressIncrement
      if (currentProgress >= targetProgress) {
        setProgress(targetProgress)
        setCurrentStepIndex(prev => prev + 1)
      } else {
        setProgress(currentProgress)
      }
    }, 100)

    return () => {
      if (stepIntervalRef.current) {
        clearInterval(stepIntervalRef.current)
      }
    }
  }, [isOpen, currentStepIndex, completed, steps])

  // Handle timer
  useEffect(() => {
    if (!isOpen) return

    timerIntervalRef.current = setInterval(() => {
      setElapsedTime(prev => prev + 1)
    }, 1000)

    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current)
      }
    }
  }, [isOpen])

  // Handle completion
  useEffect(() => {
    if (completed && isOpen) {
      setProgress(100)
      setCurrentStepIndex(steps.length)

      // Auto-close after 500ms
      const closeTimeout = setTimeout(() => {
        onClose()
      }, 500)

      return () => clearTimeout(closeTimeout)
    }
  }, [completed, isOpen, onClose, steps.length])

  // Handle Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
    }

    return () => {
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen, onClose])

  // Handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  if (!isOpen) return null

  const getStepStatus = (index: number): 'complete' | 'in-progress' | 'pending' => {
    if (index < currentStepIndex) return 'complete'
    if (index === currentStepIndex) return 'in-progress'
    return 'pending'
  }

  const getStepIcon = (status: 'complete' | 'in-progress' | 'pending'): string => {
    switch (status) {
      case 'complete': return '✓'
      case 'in-progress': return '⏳'
      case 'pending': return '○'
    }
  }

  const getHeaderText = () => {
    switch (mode) {
      case 'Strategy Map Regeneration': return 'Regenerating Strategy Map...'
      case 'Strategy Planning': return 'Analyzing Your Query...'
      default: return 'Processing...'
    }
  }

  const getModeLabel = () => {
    switch (mode) {
      case 'Strategy Map Regeneration': return 'Strategy Map Regeneration'
      case 'Strategy Planning': return 'Strategy Planning Workflow'
      default: return `${mode} Response`
    }
  }

  return (
    <div className="modal-backdrop" onClick={handleBackdropClick}>
      <div ref={modalRef} className="progress-modal-container">
        <div className="progress-modal-header">
          <h3>{getHeaderText()}</h3>
          <button className="progress-modal-close-btn" onClick={onClose} title="Close (Esc)">
            ✕
          </button>
        </div>
        <div className="progress-modal-content">
          <div className="progress-mode-label">{getModeLabel()}</div>

          <div className="progress-bar-container">
            <div className="progress-bar-fill" style={{ width: `${progress}%` }}></div>
          </div>
          <div className="progress-percentage">{Math.round(progress)}%</div>

          <div className="progress-steps">
            {steps.map((step, index) => {
              const status = getStepStatus(index)
              return (
                <div key={index} className={`progress-step progress-step-${status}`}>
                  <span className="progress-step-icon">{getStepIcon(status)}</span>
                  <span className="progress-step-label">{step.label}</span>
                </div>
              )
            })}
          </div>

          <div className="progress-elapsed">Elapsed: {elapsedTime}s</div>
        </div>
      </div>
    </div>
  )
}
